import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { attributionPillSx, textLinkSx } from './CaseStudies';
import PageLinkItem from '../components/PageLinkItem';
import MonoChips from '../components/stitch/MonoChips';
import PageHero from '../components/stitch/PageHero';
import { cardSx } from '../components/stitch/Section';
import projects from '../data/Projects';
import { tokens } from '../theme';

// Built to the Stitch "Selected Work" screen (project 1587770953174176333), with the site's own
// copy. The full archive stays on the page; Stitch showed a sample of it.
const deliveredProjects = projects.filter(
  (project) => project.attribution === 'Scaled Tech delivery'
);
const earlierProjects = projects.filter((project) => project.attribution === 'Earlier experience');
const selectedStudies = [
  ...deliveredProjects.map((project) => ({
    title: project.title,
    attribution: project.attribution,
    description: project.description,
    path: '/case-studies/venue-workflow',
    tags: project.tags as readonly string[] | undefined,
  })),
  {
    title: 'Shared multi-site publishing',
    attribution: 'Delivered through our predecessor software practice',
    description:
      'One CMS gave staff a shared place to manage distinct branded sites, with separate content, navigation, editorial access, and multilingual publishing.',
    path: '/case-studies/multi-site-publishing',
    tags: undefined,
  },
  {
    title: 'Multi-level approval for administrative briefings',
    attribution: 'Earlier public-sector work, client anonymous',
    description:
      'Briefings moved through department, division, and executive approval levels, with configurable approvers, notifications, and an auditable path to publication.',
    path: '/case-studies/staged-review',
    tags: undefined,
  },
];

const bandSx = {
  mt: { xs: 5, md: 8 },
  p: { xs: 3, md: 5 },
  borderRadius: 3,
  bgcolor: tokens.surfaceLow,
} as const;

const inlineLinkSx = { color: tokens.accentText, fontWeight: 600 } as const;

function SectionHeading({ id, label, title }: { id: string; label: string; title: string }) {
  return (
    <>
      <Typography
        variant='labelCaps'
        component='p'
        sx={{ color: tokens.accentText }}
      >
        {label}
      </Typography>
      <Typography
        id={id}
        component='h2'
        variant='h3'
        sx={{ mt: 1 }}
      >
        {title}
      </Typography>
    </>
  );
}

function Solutions() {
  return (
    <Container sx={{ overflowWrap: 'anywhere' }}>
      <PageHero
        eyebrow='Portfolio'
        title='Selected work'
        intro='Start with these examples of publishing and workflow tools. Each identifies who delivered the work; the archive below preserves earlier projects and collaborations.'
      >
        <Box
          component={Link}
          to='/case-studies'
          sx={{ ...textLinkSx, mt: 3 }}
        >
          Read the CMS case studies
          <ArrowForwardIcon
            aria-hidden='true'
            sx={{ fontSize: 18 }}
          />
        </Box>
      </PageHero>

      <Box
        component='section'
        aria-labelledby='selected-case-studies'
        sx={{ ...bandSx, mt: 0 }}
      >
        <SectionHeading
          id='selected-case-studies'
          label='Delivered work'
          title='Selected case studies'
        />
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 3,
          }}
        >
          {selectedStudies.map((study) => (
            <Box
              component='article'
              key={study.path}
              sx={{
                ...cardSx,
                minWidth: 0,
                p: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant='labelCaps'
                component='p'
                sx={{ ...attributionPillSx, alignSelf: 'flex-start' }}
              >
                {study.attribution}
              </Typography>
              <Typography
                component='h3'
                variant='h4'
                sx={{ mt: 2 }}
              >
                {study.title}
              </Typography>
              <Typography
                variant='body2'
                sx={{ mt: 1.5 }}
              >
                {study.description}
              </Typography>
              <Box sx={{ mt: 'auto', pt: 3, display: 'grid', gap: 2.5 }}>
                {study.tags && (
                  <Box>
                    <Typography
                      variant='bodySm'
                      component='p'
                      sx={{ color: tokens.onSurfaceVariant, mb: 1 }}
                    >
                      Technologies used:
                    </Typography>
                    <MonoChips
                      items={study.tags}
                      commas
                    />
                  </Box>
                )}
                <Box
                  component={Link}
                  to={study.path}
                  aria-label={`Read case study: ${study.title}`}
                  sx={textLinkSx}
                >
                  Read case study
                  <ArrowForwardIcon
                    aria-hidden='true'
                    sx={{ fontSize: 18 }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            mt: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            bgcolor: tokens.surfaceHigh,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <InfoOutlinedIcon
              aria-hidden='true'
              sx={{ color: tokens.accentText, mt: '2px' }}
            />
            <Typography variant='body1'>
              Other earlier work includes{' '}
              <Box
                component={Link}
                to='/case-studies/handbook-publishing'
                sx={inlineLinkSx}
              >
                authenticated handbook publishing
              </Box>{' '}
              and{' '}
              <Box
                component={Link}
                to='/case-studies/configurable-content'
                sx={inlineLinkSx}
              >
                configurable content and forms
              </Box>
              . The case studies explain the contribution and supported results.
            </Typography>
          </Box>
          <Box
            component={Link}
            to='/cms-governance'
            sx={{ ...textLinkSx, flexShrink: 0 }}
          >
            Explore CMS governance
            <ArrowForwardIcon
              aria-hidden='true'
              sx={{ fontSize: 18 }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='ongoing-development'
        sx={{ mt: { xs: 5, md: 8 }, maxWidth: '56rem', mx: 'auto' }}
      >
        <Typography
          id='ongoing-development'
          component='h2'
          variant='labelCaps'
          sx={{ display: 'block', color: tokens.accentText, textAlign: 'center' }}
        >
          Ongoing development
        </Typography>
        <Box
          sx={{
            ...cardSx,
            mt: 2,
            p: { xs: 3, md: 5 },
            borderLeft: `6px solid ${tokens.primaryFixed}`,
          }}
        >
          <Typography
            variant='labelCaps'
            component='p'
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: tokens.primaryFixed,
              color: tokens.primary,
            }}
          >
            <Box
              component='span'
              aria-hidden='true'
              sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tokens.secondary }}
            />
            In progress
          </Typography>
          <Typography
            component='h3'
            variant='h3'
            sx={{ mt: 2 }}
          >
            InkFlow Studio
          </Typography>
          <Typography
            variant='bodyLg'
            component='p'
            sx={{ mt: 1.5 }}
          >
            A local editorial workspace for Markdown content and media, with preview and static
            export. Its transition from WordPress to Cloudflare publishing is still in progress.
          </Typography>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='earlier-experience'
        sx={bandSx}
      >
        <Box sx={{ maxWidth: '46rem' }}>
          <SectionHeading
            id='earlier-experience'
            label='Archive'
            title='Earlier experience and collaborations'
          />
          <Typography
            variant='body2'
            sx={{ mt: 2 }}
          >
            This archive records work from our principal&apos;s predecessor practice and earlier
            collaborations. Descriptions reflect the contribution made at the time; they do not
            establish that a site remains active or that its current version is our work. Historical
            client identities remain anonymous where publication has not been cleared.
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 1.5 }}
          >
            Earlier property marketing administration also used a central hub for separate property
            sites, with per-site configuration, language content, domains, and scoped editor roles.
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          {earlierProjects.map((project) => (
            <Box
              component='article'
              key={project.title}
              sx={{
                ...cardSx,
                minWidth: 0,
                p: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                component='h3'
                variant='h5'
              >
                {project.title}
              </Typography>
              <Typography
                variant='bodySm'
                component='p'
                sx={{ mt: 1, color: tokens.onSurfaceVariant }}
              >
                {project.description}
              </Typography>
              <Box sx={{ mt: 'auto', pt: 2.5 }}>
                <Typography
                  variant='bodySm'
                  component='p'
                  sx={{ color: tokens.onSurfaceVariant, mb: 1 }}
                >
                  Technologies used at the time:
                </Typography>
                <MonoChips
                  items={project.tags}
                  commas
                />
                {project.link && (
                  <PageLinkItem
                    third={project.link}
                    url={project.link}
                    type='pageLink'
                    sx={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      overflowWrap: 'anywhere',
                      mt: 1.5,
                      ml: '-8px',
                      px: 1,
                      py: 0.5,
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
}

export default Solutions;
